import useDownloadGeneratedContracts from "@/modules/property-tracks/hooks/tenants/download-files";
import { Box, IconButton, Typography, useTheme, Menu, MenuItem } from "@mui/material";
import { format } from "date-fns";
import { MoreVertical } from "lucide-react";
import { useState } from "react";
import ContractStatusBadge from "../contracts-status-badge";
import { ContractListItem } from "@/modules/property-tracks/dtos/contracts/index";
import { EnvironmentVariables } from "@/modules/common/types";

const ContractItem = ({ contract }: { contract: ContractListItem }) => {
  const theme = useTheme();
  const { download, isDownloading } = useDownloadGeneratedContracts();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = () => {
    download({ contractUids: [contract.uid] });
    handleMenuClose();
  };

  const handleViewContract = () => {
    const contractType = contract.contractTemplate.contractType;
    const queryParams = new URLSearchParams({
      contractUid: contract.uid,
      viewContract: "true",
    });

    if (contractType && ['Owner', 'Tenant', 'Developer'].includes(contractType)) {
      queryParams.append("contractType", contractType);
    }

    const contractsUrl = `${EnvironmentVariables.CONTRACTS_FRONTEND_URL}/contracts?${queryParams.toString()}`;
    window.open(contractsUrl, "_blank");
    handleMenuClose();
  };

  const handleGenerateContract = () => {
    const contractType = contract.contractTemplate.contractType;
    const queryParams = new URLSearchParams({
      contractUid: contract.uid,
      editContract: "true",
    });

    if (contractType && ['Owner', 'Tenant', 'Developer'].includes(contractType)) {
      queryParams.append("contractType", contractType);
    }

    const contractsUrl = `${EnvironmentVariables.CONTRACTS_FRONTEND_URL}/contracts?${queryParams.toString()}`;
    window.open(contractsUrl, "_blank");
    handleMenuClose();
  };

  const handleSignContract = () => {
    const contractType = contract.contractTemplate.contractType;
    const queryParams = new URLSearchParams({
      contractUid: contract.uid,
      signContract: "true",
    });

    if (contractType && ['Owner', 'Tenant', 'Developer'].includes(contractType)) {
      queryParams.append("contractType", contractType);
    }

    const contractsUrl = `${EnvironmentVariables.CONTRACTS_FRONTEND_URL}/contracts?${queryParams.toString()}`;
    window.open(contractsUrl, "_blank");
    handleMenuClose();
  };

  const displayDate = contract.generatedAt || contract.createdAt;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
          {contract.contractTemplate.name}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          {displayDate && (
            <Typography
              variant="caption"
              color={theme.nawy.colors.semantic.text.secondary}
              sx={{
                ...theme.nawy.typography.english.variants.body.xs.regular,
              }}
            >
              {format(new Date(displayDate), "dd MMMM yyyy")}
            </Typography>
          )}
          <ContractStatusBadge status={contract.status} />
        </Box>
      </Box>

      <IconButton
        onClick={handleMenuOpen}
        disabled={isDownloading}
        sx={{ mr: 1 }}
      >
        <MoreVertical
          size={16}
          color={theme.nawy.colors.primitives.blue[500]}
        />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: theme.nawy.radius.lg,
              minWidth: 140,
            },
          },
        }}
      >
        {contract.status === "draft" && (
          <MenuItem onClick={handleGenerateContract}>Generate</MenuItem>
        )}
        {contract.status === "pending_signature" && (
          <MenuItem onClick={handleSignContract}>Sign</MenuItem>
        )}
        <MenuItem onClick={handleViewContract}>View Contract</MenuItem>
        <MenuItem onClick={handleDownload}>Download</MenuItem>
      </Menu>
    </Box>
  );
};

export default ContractItem;

